import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

const API = 'https://api.glowytics.com';

const PIE_COLORS = { cheaper: '#059669', equal: '#9ca3af', pricier: '#dc2626' };

// Dilim içine yüzde yazan label (sadece yeterince büyük dilimlerde)
function renderSliceLabel({ cx, cy, midAngle, innerRadius, outerRadius, percent }) {
  if (percent < 0.06) return null;
  const RAD = Math.PI / 180;
  const r = innerRadius + (outerRadius - innerRadius) * 0.5;
  const x = cx + r * Math.cos(-midAngle * RAD);
  const y = cy + r * Math.sin(-midAngle * RAD);
  return (
    <text x={x} y={y} fill="#fff" fontSize={12} fontWeight={700}
      textAnchor="middle" dominantBaseline="central">
      {`${(percent * 100).toFixed(0)}%`}
    </text>
  );
}

// Küçük donut için dilim içi yüzde label'ı (yalnızca >=15% dilimlerde, küçük font)
function renderMiniLabel({ cx, cy, midAngle, innerRadius, outerRadius, percent }) {
  if (percent < 0.15) return null;
  const RAD = Math.PI / 180;
  const r = innerRadius + (outerRadius - innerRadius) * 0.5;
  const x = cx + r * Math.cos(-midAngle * RAD);
  const y = cy + r * Math.sin(-midAngle * RAD);
  return (
    <text x={x} y={y} fill="#fff" fontSize={9} fontWeight={700}
      textAnchor="middle" dominantBaseline="central">
      {`${(percent * 100).toFixed(0)}`}
    </text>
  );
}

// Tablo satırı içi küçük donut — cheaper/equal/pricier dağılımı (legend yok, hover'da tooltip)
function MiniDonut({ cheaper = 0, equal = 0, pricier = 0, size = 64 }) {
  const total = cheaper + equal + pricier;
  const data = [
    { key: 'cheaper', name: 'Cheaper', value: cheaper, color: PIE_COLORS.cheaper },
    { key: 'equal', name: 'Equal', value: equal, color: PIE_COLORS.equal },
    { key: 'pricier', name: 'More expensive', value: pricier, color: PIE_COLORS.pricier },
  ].filter(d => d.value > 0);
  if (!total) return <span className="text-[11px] text-gray-300">–</span>;
  return (
    <div style={{ width: size, height: size }}>
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          <Pie data={data} dataKey="value" nameKey="name" cx="50%" cy="50%"
            innerRadius={size * 0.26} outerRadius={size * 0.5} paddingAngle={1} stroke="none"
            labelLine={false} label={renderMiniLabel}>
            {data.map(d => <Cell key={d.key} fill={d.color} />)}
          </Pie>
          <Tooltip
            formatter={(val, name) => [`${Number(val).toLocaleString('en-US')} (${((val / total) * 100).toFixed(0)}%)`, name]}
          />
        </PieChart>
      </ResponsiveContainer>
    </div>
  );
}

function getBrandId() {
  return (
    localStorage.getItem('currentBrandId') ||
    sessionStorage.getItem('currentBrandId') ||
    localStorage.getItem('selectedBrandId') ||
    ''
  );
}

const fmtPrice = (v, cur) => {
  if (v == null || v === '') return '-';
  const n = Number(v);
  if (!isFinite(n)) return '-';
  return `${n.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}${cur ? ' ' + cur : ''}`;
};
const fmtPct = v => (v == null ? '-' : `${v > 0 ? '+' : ''}${Number(v).toFixed(1)}%`);
const fmtNum = v => (v == null ? '-' : Number(v).toLocaleString('en-US'));

export default function MerchantCenterPriceBenchmark() {
  const brandId = useMemo(() => getBrandId(), []);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [resyncing, setResyncing] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [pdfExporting, setPdfExporting] = useState(false);
  const donutRef = useRef(null);
  const donutCellRefs = useRef({}); // brand → mini-donut DOM node (for PDF capture)
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all'); // all | cheaper | pricier
  const [brandFilter, setBrandFilter] = useState('all');
  const [sortBy, setSortBy] = useState('delta_desc'); // delta_desc | delta_asc | price_desc | price_asc

  const load = useCallback(async () => {
    if (!brandId) { setError('No active brand found.'); setLoading(false); return; }
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API}/api/auth/merchant-center/price-benchmark-list?country=TR`, {
        headers: { 'Content-Type': 'application/json', 'X-Brand-ID': brandId },
        credentials: 'include',
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json?.error || 'Failed to load data');
      setData(json);
    } catch (e) {
      setError(e?.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [brandId]);

  useEffect(() => { load(); }, [load]);

  const resync = async () => {
    if (resyncing) return;
    setResyncing(true);
    setError('');
    try {
      const res = await fetch(`${API}/api/auth/merchant-center/sync/price-competitiveness`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Brand-ID': brandId },
        credentials: 'include',
        body: JSON.stringify({ country: 'TR' }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json?.error || 'Sync failed');
      await load();
    } catch (e) {
      setError(e?.message || 'Sync failed');
    } finally {
      setResyncing(false);
    }
  };

  const exportExcel = async () => {
    if (exporting || !brandId) return;
    setExporting(true);
    setError('');
    try {
      const res = await fetch(`${API}/api/auth/merchant-center/price-benchmark-export?country=TR`, {
        headers: { 'X-Brand-ID': brandId },
        credentials: 'include',
      });
      if (!res.ok) {
        let msg = 'Export failed';
        try { msg = (await res.json())?.error || msg; } catch (_) {}
        throw new Error(msg);
      }
      const blob = await res.blob();
      const cd = res.headers.get('Content-Disposition') || '';
      const m = cd.match(/filename="?([^"]+)"?/);
      const fname = m ? m[1] : 'price-benchmark.xlsx';
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fname;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      setError(e?.message || 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  // Distinct brands for the brand filter dropdown
  const brandOptions = useMemo(() => {
    const set = new Set();
    (data?.rows || []).forEach(r => { if (r.brand) set.add(r.brand); });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [data]);

  const rows = useMemo(() => {
    let r = data?.rows || [];
    const q = search.trim().toLowerCase();
    if (q) r = r.filter(x =>
      (x.title || '').toLowerCase().includes(q) ||
      (x.brand || '').toLowerCase().includes(q) ||
      (x.offerId || '').toLowerCase().includes(q) ||
      (x.productId || '').toLowerCase().includes(q)
    );
    if (brandFilter !== 'all') r = r.filter(x => (x.brand || '') === brandFilter);
    if (filter === 'cheaper') r = r.filter(x => x.deltaPct != null && x.deltaPct < 0);
    if (filter === 'pricier') r = r.filter(x => x.deltaPct != null && x.deltaPct > 0);
    const s = [...r];
    s.sort((a, b) => {
      const ad = a.deltaPct ?? 0, bd = b.deltaPct ?? 0;
      const ap = a.ourPrice ?? 0, bp = b.ourPrice ?? 0;
      switch (sortBy) {
        case 'delta_asc': return ad - bd;
        case 'price_desc': return bp - ap;
        case 'price_asc': return ap - bp;
        default: return bd - ad; // delta_desc
      }
    });
    return s;
  }, [data, search, filter, brandFilter, sortBy]);

  const stats = data?.stats || {};

  // Filtreye göre canlı istatistik — görünen (filtrelenmiş) satırlardan hesaplanır
  const isBrandFiltered = brandFilter !== 'all';
  const filterActive = isBrandFiltered || filter !== 'all' || search.trim() !== '';
  const view = useMemo(() => {
    const withDelta = rows.filter(r => r.deltaPct != null);
    const cheaper = withDelta.filter(r => r.deltaPct < 0).length;
    const pricier = withDelta.filter(r => r.deltaPct > 0).length;
    const equal = withDelta.filter(r => r.deltaPct === 0).length;
    const avgDelta = withDelta.length ? withDelta.reduce((s, r) => s + r.deltaPct, 0) / withDelta.length : null;
    return { withBenchmark: rows.length, cheaper, pricier, equal, avgDelta };
  }, [rows]);
  // Fiyat konumu dağılımı (pie) — görünen benchmark'lı ürünler üzerinden, aktif filtreyi yansıtır
  const pieData = useMemo(() => ([
    { key: 'cheaper', name: 'Cheaper than market', value: view.cheaper, color: PIE_COLORS.cheaper },
    { key: 'equal', name: 'Equal to market', value: view.equal, color: PIE_COLORS.equal },
    { key: 'pricier', name: 'More expensive', value: view.pricier, color: PIE_COLORS.pricier },
  ].filter(d => d.value > 0)), [view]);
  const pieTotal = view.cheaper + view.equal + view.pricier;

  // brandTotals anahtarları preview'dan ("On"), filtre değeri comp'tan ("on") gelir → küçük harfe normalize et
  const brandTotalsLc = useMemo(() => {
    const m = {};
    Object.entries(stats.brandTotals || {}).forEach(([k, v]) => { m[String(k).toLowerCase()] = v; });
    return m;
  }, [stats.brandTotals]);
  // Marka bazlı özet: katalogdaki toplam ürün, kaçı benchmark'lı, kapsama % ve ort. fark.
  // benchmark'lı sayılar tüm snapshot'tan (filtre uygulanmadan) gelir.
  const brandSummary = useMemo(() => {
    const all = data?.rows || [];
    const m = new Map();
    for (const r of all) {
      const b = (r.brand || 'Unbranded').trim() || 'Unbranded';
      if (!m.has(b)) m.set(b, { brand: b, benchmarked: 0, cheaper: 0, pricier: 0, equal: 0, sumDelta: 0, deltaN: 0 });
      const e = m.get(b);
      e.benchmarked++;
      if (r.deltaPct != null) {
        e.deltaN++; e.sumDelta += r.deltaPct;
        if (r.deltaPct < 0) e.cheaper++; else if (r.deltaPct > 0) e.pricier++; else e.equal++;
      }
    }
    const arr = Array.from(m.values()).map(e => {
      const total = brandTotalsLc[e.brand.toLowerCase()] ?? null;
      return {
        ...e,
        total,
        coverage: (total && total > 0) ? (e.benchmarked / total) * 100 : null,
        avgDelta: e.deltaN ? e.sumDelta / e.deltaN : null,
      };
    });
    arr.sort((a, b) => b.benchmarked - a.benchmarked);
    return arr;
  }, [data, brandTotalsLc]);

  // Toplam ürün paydası: marka filtresi varsa o markanın kataloğu, yoksa tüm katalog
  const totalForView = isBrandFiltered
    ? (brandTotalsLc[brandFilter.toLowerCase()] ?? null)
    : (stats.totalProducts ?? null);
  const coverageForView = (totalForView && totalForView > 0) ? (view.withBenchmark / totalForView) * 100 : null;

  // PDF export: Price Position donut (görsel) + Coverage by Brand (seçilebilir tablo)
  const exportPDF = async () => {
    if (pdfExporting) return;
    setPdfExporting(true);
    setError('');
    try {
      const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
      const pageW = pdf.internal.pageSize.getWidth();
      const margin = 14;
      let y = margin;

      // Başlık + meta
      pdf.setFont('helvetica', 'bold'); pdf.setFontSize(16); pdf.setTextColor(17, 24, 39);
      pdf.text('Price Competitiveness Benchmark', margin, y); y += 7;
      pdf.setFont('helvetica', 'normal'); pdf.setFontSize(9); pdf.setTextColor(120, 120, 120);
      const scope = isBrandFiltered ? `Brand: ${brandFilter}` : 'All brands';
      pdf.text(`${scope}   ·   Merchant: ${data?.merchantId || '-'}   ·   Country: ${data?.country || '-'}   ·   Updated: ${data?.fetchDate || '-'}`, margin, y);
      y += 4;
      pdf.text(`Generated: ${new Date().toLocaleString('en-GB')}`, margin, y);
      y += 8;

      // Price Position — donut görseli + sağında özet
      if (donutRef.current && pieTotal > 0) {
        pdf.setFont('helvetica', 'bold'); pdf.setFontSize(12); pdf.setTextColor(17, 24, 39);
        pdf.text('Price Position', margin, y); y += 5;

        const canvas = await html2canvas(donutRef.current, { scale: 2, backgroundColor: '#ffffff', logging: false, useCORS: true });
        const imgW = 85;
        const imgH = (canvas.height * imgW) / canvas.width;
        pdf.addImage(canvas.toDataURL('image/png'), 'PNG', margin, y, imgW, imgH);

        // Sağ kolon özet satırları
        const pct = n => pieTotal ? `${((n / pieTotal) * 100).toFixed(1)}%` : '-';
        const lines = [
          { c: PIE_COLORS.cheaper, label: 'Cheaper than market', v: view.cheaper },
          { c: PIE_COLORS.equal, label: 'Equal to market', v: view.equal },
          { c: PIE_COLORS.pricier, label: 'More expensive', v: view.pricier },
        ];
        let ly = y + 6;
        const lx = margin + imgW + 8;
        pdf.setFontSize(10);
        pdf.setFont('helvetica', 'bold'); pdf.setTextColor(17, 24, 39);
        pdf.text(`${fmtNum(pieTotal)} benchmarked products`, lx, ly); ly += 7;
        pdf.setFont('helvetica', 'normal');
        lines.forEach(l => {
          const [r, g, b] = [parseInt(l.c.slice(1, 3), 16), parseInt(l.c.slice(3, 5), 16), parseInt(l.c.slice(5, 7), 16)];
          pdf.setFillColor(r, g, b);
          pdf.circle(lx + 1.5, ly - 1.4, 1.5, 'F');
          pdf.setTextColor(55, 65, 81);
          pdf.text(`${l.label}:  ${fmtNum(l.v)}  (${pct(l.v)})`, lx + 5, ly);
          ly += 6;
        });
        y += imgH + 6;
      }

      // Coverage by Brand — vektör tablo + her markanın mini donut'u (görsel)
      if (brandSummary.length > 0) {
        // Ekrandaki mini donutları yakala (marka → PNG)
        const splitImages = {};
        for (const b of brandSummary) {
          const el = donutCellRefs.current[b.brand];
          if (el) {
            try {
              const c = await html2canvas(el, { scale: 2, backgroundColor: null, logging: false, useCORS: true });
              splitImages[b.brand] = c.toDataURL('image/png');
            } catch (_) { /* tek donut yakalanamazsa atla */ }
          }
        }

        pdf.setFont('helvetica', 'bold'); pdf.setFontSize(12); pdf.setTextColor(17, 24, 39);
        pdf.text('Coverage by Brand', margin, y); y += 2;
        const SPLIT_COL = 4;
        autoTable(pdf, {
          startY: y + 2,
          head: [['Brand', 'Catalog', 'Benchmarked', 'Coverage', 'Split', 'Cheaper', 'Equal', 'Pricier', 'Avg Diff']],
          body: brandSummary.map(b => [
            b.brand,
            b.total != null ? fmtNum(b.total) : '-',
            fmtNum(b.benchmarked),
            b.coverage != null ? `${b.coverage.toFixed(0)}%` : '-',
            '', // Split — görsel didDrawCell ile çizilir
            fmtNum(b.cheaper),
            fmtNum(b.equal),
            fmtNum(b.pricier),
            b.avgDelta == null ? '-' : fmtPct(b.avgDelta),
          ]),
          styles: { fontSize: 8, cellPadding: 1.6, valign: 'middle', minCellHeight: 13 },
          headStyles: { fillColor: [37, 99, 235], textColor: 255, fontStyle: 'bold' },
          alternateRowStyles: { fillColor: [248, 250, 252] },
          columnStyles: {
            0: { halign: 'left' },
            1: { halign: 'right' }, 2: { halign: 'right' }, 3: { halign: 'left' },
            4: { halign: 'center', cellWidth: 16 },
            5: { halign: 'right', textColor: [5, 150, 105] },
            6: { halign: 'right' },
            7: { halign: 'right', textColor: [220, 38, 38] },
            8: { halign: 'right' },
          },
          margin: { left: margin, right: margin },
          didDrawCell: (hook) => {
            if (hook.section !== 'body' || hook.column.index !== SPLIT_COL) return;
            const brand = brandSummary[hook.row.index]?.brand;
            const img = brand && splitImages[brand];
            if (!img) return;
            const size = Math.min(11, hook.cell.height - 1.5);
            const x = hook.cell.x + (hook.cell.width - size) / 2;
            const yy = hook.cell.y + (hook.cell.height - size) / 2;
            pdf.addImage(img, 'PNG', x, yy, size, size);
          },
        });
      }

      const fname = `price-benchmark-${isBrandFiltered ? brandFilter.replace(/[^\w]+/g, '-') + '-' : ''}${data?.country || 'TR'}.pdf`;
      pdf.save(fname);
    } catch (e) {
      setError(e?.message || 'PDF export failed');
    } finally {
      setPdfExporting(false);
    }
  };

  return (
    <div className="p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header: Merchant Center logo (same as other MC pages) */}
        <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
          <div>
            <img src="/merchantcenterlogo.svg" alt="Merchant Center" className="h-10 mb-3" />
            <h2 className="text-lg font-bold text-gray-900">Price Competitiveness Benchmark</h2>
            <p className="mt-1 text-gray-600 text-sm max-w-2xl leading-relaxed">
              Google Merchant Center only returns a price benchmark (market comparison) for some products.
              This page lists <strong>only products that have a benchmark</strong> and reads instantly from the database (always ready).
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={exportPDF}
              disabled={pdfExporting || loading || !data}
              className="inline-flex items-center gap-2 bg-rose-600 hover:bg-rose-700 disabled:bg-gray-300 text-white text-sm font-semibold px-4 py-2 rounded-lg shadow-sm"
              title="Export to PDF — Price Position chart + Coverage by Brand"
            >
              {pdfExporting ? 'Exporting…' : '⬇ Export PDF'}
            </button>
            <button
              onClick={exportExcel}
              disabled={exporting || loading || !rows.length}
              className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 disabled:bg-gray-300 text-white text-sm font-semibold px-4 py-2 rounded-lg shadow-sm"
              title="Export to Excel — one sheet per brand"
            >
              {exporting ? 'Exporting…' : '⬇ Export Excel'}
            </button>
            <button
              onClick={resync}
              disabled={resyncing}
              className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 text-white text-sm font-semibold px-4 py-2 rounded-lg shadow-sm"
            >
              {resyncing ? 'Syncing…' : 'Resync'}
            </button>
          </div>
        </div>

        {/* Meta */}
        {data && (
          <div className="text-xs text-gray-500 mb-4 flex items-center gap-3 flex-wrap">
            <span>Last updated: <strong>{data.fetchDate || '-'}</strong></span>
            <span className="text-gray-300">·</span>
            <span>Merchant: {data.merchantId}</span>
            <span className="text-gray-300">·</span>
            <span>Country: {data.country}</span>
            {data.stale && <span className="px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 ring-1 ring-amber-200">Refreshing in background…</span>}
          </div>
        )}

        {/* Benchmark coverage highlight — reflects the active filter */}
        {data && (
          <div className="mb-5 bg-blue-50 border border-blue-200 rounded-xl px-4 py-3 flex items-center gap-3 flex-wrap">
            <span className="text-sm text-blue-900">
              {filterActive && <span className="font-semibold">Filtered: </span>}
              Google provides a benchmark for{' '}
              <strong>{fmtNum(view.withBenchmark)}</strong>
              {totalForView != null
                ? <> of {isBrandFiltered ? <strong>{brandFilter}</strong> : 'your'} <strong>{fmtNum(totalForView)}</strong> products
                    {coverageForView != null && <> — <strong>{coverageForView.toFixed(1)}%</strong> coverage</>}.</>
                : <> products{isBrandFiltered ? ` for ${brandFilter}` : ''}.</>}
            </span>
            {coverageForView != null && (
              <div className="flex-1 min-w-[160px] h-2 bg-blue-100 rounded-full overflow-hidden max-w-xs">
                <div className="h-full bg-blue-600 rounded-full" style={{ width: `${Math.min(100, coverageForView)}%` }} />
              </div>
            )}
          </div>
        )}

        {/* KPI — values follow the active filter */}
        {data && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-5">
            {[
              { label: isBrandFiltered ? `Total Products · ${brandFilter}` : 'Total Products', value: totalForView != null ? fmtNum(totalForView) : '-', sub: isBrandFiltered ? 'brand catalog' : 'in Merchant Center' },
              { label: 'With Benchmark', value: fmtNum(view.withBenchmark), sub: coverageForView != null ? `${coverageForView.toFixed(1)}% of ${isBrandFiltered ? 'brand' : 'catalog'}` : (filterActive ? 'in current filter' : null) },
              { label: 'Average Difference', value: view.avgDelta == null ? '-' : fmtPct(view.avgDelta), color: (view.avgDelta ?? 0) > 0 ? 'text-red-600' : 'text-emerald-600' },
              { label: 'Cheaper (vs market)', value: fmtNum(view.cheaper), color: 'text-emerald-600' },
              { label: 'More Expensive (vs market)', value: fmtNum(view.pricier), color: 'text-red-600' },
            ].map(k => (
              <div key={k.label} className="bg-white border border-gray-200 rounded-xl shadow-sm p-4">
                <p className="text-[11px] text-gray-500">{k.label}</p>
                <p className={`text-xl font-bold mt-1 ${k.color || 'text-gray-900'}`}>{k.value}</p>
                {k.sub && <p className="text-[10px] text-gray-400 mt-0.5">{k.sub}</p>}
              </div>
            ))}
          </div>
        )}

        {/* Price position breakdown (donut) + per-brand coverage table */}
        {data && (pieTotal > 0 || brandSummary.length > 0) && (
          <div className="mb-5 grid grid-cols-1 lg:grid-cols-3 gap-4">

          {/* Price position donut — reflects the active filter */}
          {pieTotal > 0 && (
            <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden lg:col-span-1">
              <div className="px-4 py-3 border-b border-gray-100">
                <h3 className="text-sm font-bold text-gray-900">Price Position{isBrandFiltered ? ` · ${brandFilter}` : ''}</h3>
                <p className="text-[11px] text-gray-400 mt-0.5">{fmtNum(pieTotal)} benchmarked product{pieTotal === 1 ? '' : 's'} vs. market.</p>
              </div>
              <div className="relative p-2" ref={donutRef}>
                <ResponsiveContainer width="100%" height={320}>
                  <PieChart>
                    <Pie
                      data={pieData}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="46%"
                      innerRadius={74}
                      outerRadius={116}
                      paddingAngle={2}
                      stroke="none"
                      labelLine={false}
                      label={renderSliceLabel}
                    >
                      {pieData.map(d => <Cell key={d.key} fill={d.color} />)}
                    </Pie>
                    <Tooltip
                      formatter={(val, name) => [`${fmtNum(val)} (${((val / pieTotal) * 100).toFixed(1)}%)`, name]}
                    />
                    <Legend
                      verticalAlign="bottom"
                      iconType="circle"
                      formatter={(value) => <span className="text-[12px] text-gray-600">{value}</span>}
                    />
                  </PieChart>
                </ResponsiveContainer>
                {/* Center label */}
                <div className="pointer-events-none absolute inset-x-0 top-[120px] flex flex-col items-center">
                  <span className="text-3xl font-bold text-gray-900 tabular-nums leading-none">{fmtNum(pieTotal)}</span>
                  <span className="text-[11px] text-gray-400 mt-1">benchmarked</span>
                </div>
              </div>
            </div>
          )}

          {/* Per-brand coverage breakdown */}
          {brandSummary.length > 0 && (
          <div className={`bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden ${pieTotal > 0 ? 'lg:col-span-2' : 'lg:col-span-3'}`}>
            <div className="px-4 py-3 border-b border-gray-100">
              <h3 className="text-sm font-bold text-gray-900">Coverage by Brand</h3>
              <p className="text-[11px] text-gray-400 mt-0.5">Catalog products vs. how many Google returns a benchmark for. Click a row to filter the table below.</p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 text-gray-500 text-xs uppercase tracking-wider">
                    <th className="text-left px-4 py-2.5 font-semibold">Brand</th>
                    <th className="text-right px-4 py-2.5 font-semibold">Catalog</th>
                    <th className="text-right px-4 py-2.5 font-semibold">Benchmarked</th>
                    <th className="text-left px-4 py-2.5 font-semibold w-40">Coverage</th>
                    <th className="text-center px-4 py-2.5 font-semibold w-20">Split</th>
                    <th className="text-right px-4 py-2.5 font-semibold">Cheaper</th>
                    <th className="text-right px-4 py-2.5 font-semibold">Pricier</th>
                    <th className="text-right px-4 py-2.5 font-semibold">Avg Diff</th>
                  </tr>
                </thead>
                <tbody>
                  {brandSummary.map(b => {
                    const active = brandFilter === b.brand;
                    return (
                      <tr
                        key={b.brand}
                        onClick={() => setBrandFilter(active ? 'all' : b.brand)}
                        className={`border-t border-gray-100 cursor-pointer ${active ? 'bg-blue-50' : 'hover:bg-gray-50'}`}
                      >
                        <td className="px-4 py-2.5 font-medium text-gray-800">{b.brand}</td>
                        <td className="px-4 py-2.5 text-right tabular-nums text-gray-600">{b.total != null ? fmtNum(b.total) : '-'}</td>
                        <td className="px-4 py-2.5 text-right tabular-nums font-semibold text-gray-900">{fmtNum(b.benchmarked)}</td>
                        <td className="px-4 py-2.5">
                          <div className="flex items-center gap-2">
                            <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden min-w-[60px]">
                              <div className="h-full bg-blue-600 rounded-full" style={{ width: `${Math.min(100, b.coverage ?? 0)}%` }} />
                            </div>
                            <span className="text-[11px] text-gray-500 tabular-nums w-10 text-right">{b.coverage != null ? `${b.coverage.toFixed(0)}%` : '-'}</span>
                          </div>
                        </td>
                        <td className="px-4 py-2.5">
                          <div className="flex justify-center">
                            <div ref={el => { if (el) donutCellRefs.current[b.brand] = el; }}>
                              <MiniDonut cheaper={b.cheaper} equal={b.equal} pricier={b.pricier} />
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-2.5 text-right tabular-nums text-emerald-600">{fmtNum(b.cheaper)}</td>
                        <td className="px-4 py-2.5 text-right tabular-nums text-red-600">{fmtNum(b.pricier)}</td>
                        <td className={`px-4 py-2.5 text-right tabular-nums font-semibold ${(b.avgDelta ?? 0) > 0 ? 'text-red-600' : 'text-emerald-600'}`}>{b.avgDelta == null ? '-' : fmtPct(b.avgDelta)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
          )}

          </div>
        )}

        {/* Controls */}
        <div className="flex items-center gap-2 mb-3 flex-wrap">
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search product / brand / offer ID…"
            className="flex-1 min-w-[200px] text-sm border border-gray-200 rounded-lg px-3 py-2 outline-none focus:ring-2 focus:ring-blue-200"
          />
          <select value={brandFilter} onChange={e => setBrandFilter(e.target.value)} className="text-sm border border-gray-200 rounded-lg px-3 py-2 max-w-[200px]">
            <option value="all">All brands</option>
            {brandOptions.map(b => <option key={b} value={b}>{b}</option>)}
          </select>
          <select value={filter} onChange={e => setFilter(e.target.value)} className="text-sm border border-gray-200 rounded-lg px-3 py-2">
            <option value="all">All</option>
            <option value="cheaper">Only cheaper</option>
            <option value="pricier">Only more expensive</option>
          </select>
          <select value={sortBy} onChange={e => setSortBy(e.target.value)} className="text-sm border border-gray-200 rounded-lg px-3 py-2">
            <option value="delta_desc">Difference % (desc)</option>
            <option value="delta_asc">Difference % (asc)</option>
            <option value="price_desc">Price (desc)</option>
            <option value="price_asc">Price (asc)</option>
          </select>
        </div>

        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 text-sm">{error}</div>
        )}

        {loading ? (
          <div className="text-center py-16 text-gray-400 text-sm">Loading…</div>
        ) : !rows.length ? (
          <div className="text-center py-16 text-gray-400 text-sm">No products with a benchmark found.</div>
        ) : (
          <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 text-gray-500 text-xs uppercase tracking-wider">
                  <th className="text-left px-4 py-3 font-semibold w-14">Image</th>
                  <th className="text-left px-4 py-3 font-semibold">Product</th>
                  <th className="text-left px-4 py-3 font-semibold">Brand</th>
                  <th className="text-right px-4 py-3 font-semibold">Our Price</th>
                  <th className="text-right px-4 py-3 font-semibold">Benchmark</th>
                  <th className="text-right px-4 py-3 font-semibold">Difference</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => {
                  const neg = r.deltaPct != null && r.deltaPct < 0;
                  const pos = r.deltaPct != null && r.deltaPct > 0;
                  return (
                    <tr key={r.productId || i} className="border-t border-gray-100 hover:bg-gray-50">
                      <td className="px-4 py-2">
                        {r.imageLink
                          ? <img src={r.imageLink} alt={r.title || ''} loading="lazy" className="w-12 h-12 rounded object-cover border border-gray-200 bg-gray-50" />
                          : <div className="w-12 h-12 rounded bg-gray-100 border border-gray-200" />}
                      </td>
                      <td className="px-4 py-3">
                        {r.productLink
                          ? <a href={r.productLink} target="_blank" rel="noreferrer" className="font-medium text-blue-700 hover:underline line-clamp-1" title={r.title || ''}>{r.title || r.offerId || r.productId}</a>
                          : <div className="font-medium text-gray-800 line-clamp-1" title={r.title || ''}>{r.title || r.offerId || r.productId}</div>}
                        <div className="text-[11px] text-gray-400">{r.offerId || r.productId}</div>
                      </td>
                      <td className="px-4 py-3 text-gray-600">{r.brand || '-'}</td>
                      <td className="px-4 py-3 text-right tabular-nums">{fmtPrice(r.ourPrice, r.ourCurrency)}</td>
                      <td className="px-4 py-3 text-right tabular-nums text-gray-600">{fmtPrice(r.benchmarkPrice, r.benchmarkCurrency)}</td>
                      <td className={`px-4 py-3 text-right tabular-nums font-semibold ${neg ? 'text-emerald-600' : pos ? 'text-red-600' : 'text-gray-400'}`}>
                        {fmtPct(r.deltaPct)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        <p className="mt-3 text-[11px] text-gray-400">
          Difference = our price vs benchmark. Negative (green) = cheaper than market average, positive (red) = more expensive.
        </p>
      </div>
    </div>
  );
}
